import {FC, PropsWithChildren, ReactNode, Suspense, useEffect, useMemo} from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router';
import { ScrollToTop, LoadingPage } from '../components/panel';
import { RouterItem, RouterItemInfo } from '@interfaces/control-panel';
import Page404 from '../pages/guest/page404';
import { useAuth, useAllRoles, useRole } from '@services/hooks';
import {useMetrics, useTheme} from '@hooks/panelHooks';
import ConstructionComponent from '../components/constructor/constructionComponent';
import { PageRouting } from '@interfaces/panel/routing';
import { ThemeStyles } from '@interfaces/panel/themes';

import ConsumerRoutes from './consumerRoutes';
import { Logout } from '../pages';
import ym from 'react-yandex-metrika';
//import infos from '@components/constructor/infos/forms';
//const ConsumerRoutes = lazy(() => import('./consumerRoutes'));

export function childRoutes(routes: RouterItem, roles: string[], isAuthed: boolean, items: RouterItemInfo[] = []) {
    const { path, children = null, exact } = routes;
    const Component = routes.component;

    let elems: React.ReactNode[] = [];
    let itArr = items;

    const route_info: PageRouting = {
        code: routes.code,
        name: routes.name,
        title: routes.title,
        items: itArr
    };

    const child = Component ? (typeof Component === 'string' ? <ConstructionComponent name={Component} childProps={{ route_info }} /> : <Component route_info={route_info} />) : null;

    if (routes.needAuth === undefined || routes.needAuth === isAuthed) {
        if (routes.authRole === undefined || roles.includes(routes.authRole)) {
            if (Component) {
                // Поддержка массивов путей для v6
                const paths = Array.isArray(path) ? path : [path];
                paths.forEach((p, idx) => {
                    elems.push(
                        <Route key={`${Array.isArray(routes.path) ? routes.path[0] : routes.path}-${idx}`} path={p} element={child} />
                    );
                });
            }
            if (children) {
                itArr = items.concat({
                    name: routes.name,
                    link: Array.isArray(routes.path) ? routes.path[0] : routes.path,
                    useLink: Boolean(Component !== undefined || routes.useLink),
                    code: routes.code
                });
                children.forEach((item, idx) => {
                    const r = childRoutes(item, roles, isAuthed, itArr);
                    elems = elems.concat(r);
                });
            }
        }
    }

    return elems;
}

const defaultTheme: ThemeStyles = {
    css: [
        '/css/theme.css'
    ]
}

function insertFont(url: string) {
    const link = document.createElement("link");
    link.href = url;
    link.rel = "stylesheet";
    document.getElementsByTagName("head")[0].appendChild(link);

    //console.log('добавлен шрифт ' + url);
}

function validateStyles(styles: string[], loaderStyle?: string) {

    let cssList = [...styles];

    Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
        .filter(x => {
            if (!x?.href) return false;
            try {
                const url = new URL(x.href);
                const currentDomain = window.location.hostname;

                //Стиль загрузчика не должен быть удален
                if (loaderStyle && url.pathname.toLowerCase() === loaderStyle.toLowerCase()) {
                    return false;
                }

                // Проверка, что URL относительный или принадлежит текущему домену
                return !url.hostname || url.hostname === currentDomain;
            } catch (e) {
                return false;
            }
        }).forEach(style => {
            const pathname = new URL(style.href).pathname;
            if(pathname.startsWith("/assets/")) {
                return;
            }

            if(!cssList.includes(pathname)) {
                style.parentNode?.removeChild(style);
            }
            else {
                cssList = cssList.filter(css => css !== pathname);
            }
        });

    cssList.forEach(css => {
        const link = document.createElement("link");
        link.href = css;
        link.type = "text/css";
        link.rel = "stylesheet";
        document.getElementsByTagName("head")[0].appendChild(link);

        //console.log('добавлен стиль ' + css);
    })
}

interface ThemeSwitcherProps {
    theme?: ThemeStyles;
    loaderStyle?: string;
}

const ThemeSwitcher: FC<PropsWithChildren<ThemeSwitcherProps>> = ({ children, theme, loaderStyle }) => {

    useEffect(() => {

        if (!theme || (!theme?.css?.length && !theme.fonts?.length)) {
            return;
        }

        if(theme.css) {
            validateStyles(theme.css, theme.loaderCss ?? loaderStyle)
        }

        theme.fonts?.forEach(f => {
            insertFont(f);
        });
    }, [loaderStyle, theme]);

    return (
        <>{children}</>
    )
}

const PathHandler: FC = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const { viewPage } = useMetrics();

    useEffect(() => {
        viewPage(location.pathname);
    }, [location, viewPage]);

    return null;
}

const AppRoutes: FC = () => {

    const { isAuthed, tokenLoaded } = useAuth();
    const roles = useAllRoles();
    const theme = useTheme();

    const isConsumer = useRole('consumer');

    const themedElems: ReactNode[] = useMemo(() => {

        let e: ReactNode[] = []

        if (theme?.routes) {
            theme.routes.forEach(route => {
                e.push(childRoutes(route, roles, isAuthed));
            })
        }

        return e;
    }, [isAuthed, roles, theme]);

    //useEffect(() => {
    //    console.log(JSON.stringify(infos));
    //}, []);
    return (
        <Router >
            <PathHandler />
            <ScrollToTop>
                <Suspense fallback={<LoadingPage color='primary' />}>
                    {tokenLoaded &&
                        <Routes>
                            {isAuthed && <Route key="redtomain" path={theme?.urls.signin || "/auth"} element={<Navigate to="/" replace />} />}
                            {isAuthed && <Route key="redtomain" path={theme?.urls.signup || "/register"} element={<Navigate to="/" replace />} />}
                            {isAuthed && <Route key="redtomain" path={theme?.urls.forgot || "/forgot"} element={<Navigate to="/" replace />} />}
                            {isAuthed && (
                                <Route path='/logout' element={<Logout />} />
                            )}

                            {
                                isConsumer && (
                                    <Route path="/consumer/*" element={
                                        <ThemeSwitcher theme={defaultTheme} loaderStyle={theme?.theme?.loaderCss}>
                                            <ConsumerRoutes />
                                        </ThemeSwitcher>
                                    } />
                                )
                            }

                            <Route path="*" element={
                                <ThemeSwitcher theme={theme?.theme}>
                                    <Routes>
                                        {themedElems}
                                        {!isAuthed && <Route path="*" element={<Navigate to={theme?.urls.signin || "/auth"} replace />} />}
                                        <Route path="*" element={<Page404 />} />
                                    </Routes>
                                </ThemeSwitcher>
                            } />
                        </Routes>}
                </Suspense>
            </ScrollToTop>
        </Router>
    );

}

export default AppRoutes;